import React from "react";
import Footer from "./Footer";
import Navbar from "./Navbar";
import { v4 } from "uuid";

const PRIMARY_BLUE = 'rgb(0, 86, 210)';

const termsData = [
    {
        id: v4(),
        header: "Acceptance of Agreement",
        description: "By accessing Youdemi, you agree to these terms. If you are using the platform on behalf of a company, you represent that you have the authority to bind that entity to these terms."
    },
    {
        id: v4(),
        header: "Account Responsibility",
        description: "You are solely responsible for the activity that occurs on your account. You must keep your account password secure and notify us immediately of any breach of security."
    },
    {
        id: v4(),
        header: "Instructor Content Rights",
        description: "Instructors retain ownership of the content they create. However, by posting, you grant Youdemi a sublicensable, worldwide license to share and promote your content across our ecosystem."
    },
    {
        id: v4(),
        header: "Student License",
        description: "When you enroll in a course, you are granted a limited, non-exclusive license to view the content. You may not record, redistribute, or resell any course material without explicit permission."
    },
    {
        id: v4(),
        header: "Pricing and Lifetime Access",
        description: "Prices are subject to change. 'Lifetime access' refers to the lifetime of the product. We reserve the right to revoke access if a course is removed for legal or policy violations."
    },
    {
        id: v4(),
        header: "Refund Policy",
        description: "We offer a 30-day money-back guarantee. Refunds are not available if a significant portion of the course was consumed or if the user has requested multiple refunds in the past."
    },
    {
        id: v4(),
        header: "AI and Generated Content",
        description: "Any content generated by Youdemi’s AI tools is provided 'as-is.' Users must verify the accuracy of AI-generated code or information before using it in professional environments."
    },
    {
        id: v4(),
        header: "Academic Integrity",
        description: "Cheating, plagiarism, or sharing quiz answers is strictly prohibited. Violation of this policy may lead to the revocation of certificates and account suspension without refund."
    },
    {
        id: v4(),
        header: "Third-Party Links",
        description: "Our platform may contain links to third-party websites (like GitHub or external docs). We are not responsible for the content or privacy practices of these external sites."
    },
    {
        id: v4(),
        header: "Data Privacy",
        description: "Your privacy is vital. We collect and use your data as outlined in our Privacy Policy to improve your learning experience and process payments securely."
    },
    {
        id: v4(),
        header: "Prohibited Conduct",
        description: "You may not use the platform to transmit malware, scrape data, or harass other students. Automated access to the site (bots) is strictly forbidden unless authorized."
    },
    {
        id: v4(),
        header: "Dispute Resolution",
        description: "Most disputes can be resolved through our support team. If a formal dispute arises, you agree to resolve it through binding arbitration rather than in a general court of law."
    },
    {
        id: v4(),
        header: "Modifications to Service",
        description: "We constantly update Youdemi. We reserve the right to modify or discontinue features of the platform at any time with or without notice to you."
    },
    {
        id: v4(),
        header: "Limitation of Liability",
        description: "To the maximum extent permitted by law, Youdemi shall not be liable for any indirect, incidental, or punitive damages resulting from your use of the service."
    },
    {
        id: v4(),
        header: "Termination of Use",
        description: "We reserve the right to terminate or suspend your access to the platform at our sole discretion, without notice, for conduct that we believe violates these Terms."
    }
];

const Terms = () => {
    return (
        <React.Fragment>
            <Navbar />
            <header className="max-w-4xl mx-auto px-4 pt-16 pb-12">
                <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">Terms of Service</h1>
                <p className="text-gray-500 mb-10 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    Last updated: April 01, 2026
                </p>

                <div className="space-y-6 text-gray-700 leading-relaxed text-lg border-l-4 border-blue-500 pl-6 py-2">
                    <p>
                        Subject to these Terms of Service (this <strong>"Agreement"</strong>),
                        <strong> Youdemi Inc.</strong> ("Youdemi", "we", "us" and/or "our") provides access
                        to Youdemi's platform, mobile applications, and content delivery services.
                        By using or accessing our Services, you acknowledge that you have read, understand,
                        and agree to be bound by this Agreement.
                    </p>

                    <p>
                        If you are entering into this Agreement on behalf of a company or other legal entity,
                        you represent that you have the authority to bind such entity. If you do not have
                        such authority, or if you do not agree with this Agreement, you must not
                        accept this Agreement and may not use the Services.
                    </p>

                    <div className="bg-amber-50 border border-amber-100 p-6 rounded-2xl text-sm md:text-base shadow-sm">
                        <span className="font-bold text-amber-800 uppercase tracking-widest block mb-2">Important Notice:</span>
                        <p className="text-amber-900 font-medium">
                            PLEASE REVIEW THE MUTUAL ARBITRATION CLAUSE AND CLASS ACTION WAIVER SET FORTH IN
                            SECTION 12 CAREFULLY. IT REQUIRES YOU TO RESOLVE DISPUTES THROUGH FINAL AND
                            BINDING ARBITRATION AND WAIVES YOUR RIGHT TO PARTICIPATE IN CLASS ACTIONS.
                        </p>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 pb-24 lg:flex lg:gap-16">

                {/* Sticky Sidebar */}
                <aside className="hidden lg:block w-72 shrink-0">
                    <div className="sticky top-28 p-6 bg-gray-50 rounded-3xl border border-gray-100 shadow-sm">
                        <h3 className="font-bold text-gray-900 mb-4 px-2">On this page</h3>
                        <nav className="space-y-1">
                            {termsData.map((item, idx) => (
                                <a
                                    key={`nav-${item.id}`}
                                    href={`#term-${idx}`}
                                    className="block px-3 py-2 text-sm text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                >
                                    <span className="font-mono text-xs mr-2 opacity-50">{idx + 1}.</span>
                                    {item.header}
                                </a>
                            ))}
                        </nav>
                    </div>
                </aside>

                {/* Scrollable Terms Content */}
                <main className="flex-grow max-w-3xl space-y-20">
                    {termsData.map((item, index) => (
                        <section
                            id={`term-${index}`}
                            key={item.id}
                            className="relative pl-16 mb-16 scroll-mt-28 group"
                        >
                            <span
                                className="absolute left-0 top-0 w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white shadow-lg transition-transform group-hover:scale-110"
                                style={{ backgroundColor: PRIMARY_BLUE }}
                            >
                                {index + 1}
                            </span>
                            <h2 className="text-2xl font-bold text-gray-800 mb-4">{item.header}</h2>
                            <p className="text-gray-600 text-lg leading-relaxed">{item.description}</p>
                        </section>
                    ))}
                </main>
            </div>


            <Footer />
        </React.Fragment>
    );
};

export default Terms;